'use client'

import { api } from '@/api/v2/api'
import BBCode from '@/components/design-system/bbcode'
import RelativeTime from '@/components/design-system/relative-time'
import CoinIcon from '@/components/icons/coin'
import UserIcon from '@/components/icons/user'
import WalletIcon from '@/components/icons/wallet'
import { Link } from '@/navigation'
import type { Notification, NotificationType } from '@/types/notification'
import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import clsx from 'clsx'
import Image from 'next/image'
import './notification-item.scss'

const icons: Record<string, React.ReactElement> = {
  'deposit-pending': <WalletIcon color='#FF700F' />,
  'deposit-success': <WalletIcon color='#24D861' />,
  'withdraw-pending': <CoinIcon color='#FF700F' />,
  'withdraw-success': <CoinIcon color='#24D861' />,
  'nft-staked': <UserIcon color='#24D861' />,
  'nft-unstaked': <UserIcon color='#8C7996' />,
  user: <UserIcon />,
}

export default function NotificationItem({
  type,
  notification,
}: {
  type: NotificationType
  notification: Notification
}) {
  const queryClient = useQueryClient()

  const { mutate } = useMutation(
    async () => {
      const { data } = await api.post('/user/notifications/mark-as-read', {
        type,
        id: notification.id,
      })
      return data
    },
    {
      onMutate: () => {
        queryClient.setQueryData<
          InfiniteData<{
            items: Notification[]
            totalItems: number
            nextCursor: number | null
          }>
        >(['notifications', type], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                items: page.items.map((item) => {
                  if (item.id === notification.id) {
                    return {
                      ...item,
                      readMarked: true,
                    }
                  }

                  return item
                }),
              })),
            }
          }
        })

        queryClient.setQueryData<{
          activity: number
          alert: number
        }>(['notifications-count'], (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              [type]: oldData[type] - 1,
            }
          }
        })
      },
    }
  )

  const handleMarkAsRead = () => {
    if (notification.readMarked) {
      return
    }

    mutate()
  }

  return (
    <div
      className={clsx(
        'notification',
        `${type}-notification`,
        notification.readMarked && 'notification--read'
      )}
      onClick={handleMarkAsRead}
    >
      {notification.thumb ? (
        <div className='notification__thumbnail'>
          <Image src={notification.thumb} alt={notification.title} fill />
        </div>
      ) : (
        <div className='notification__icon'>{icons[notification.type]}</div>
      )}

      <div className='notification__content'>
        <div className='notification__title'>
          <BBCode>{notification.title}</BBCode>
        </div>
        <div className='notification__description'>
          <BBCode>{notification.description}</BBCode>
        </div>
        {notification.buttonLink && notification.buttonText && (
          <Link className='notification__button' href={notification.buttonLink}>
            <BBCode>{notification.buttonText}</BBCode>
          </Link>
        )}
      </div>

      <span className='notification__time'>
        <RelativeTime date={notification.createdAt} fromNow />
      </span>
    </div>
  )
}
